import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Activity,
  AlertTriangle,
  Building2,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  RefreshCw,
  ScrollText,
} from "lucide-react";

import Badge from "../../Components/Badge";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import SearchInput from "../../Components/Search/SearchInput";
import Select from "../../Components/Select";
import Table from "../../Components/Table";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchIbHealthCheckRepairJob,
  runIbHealthCheck,
  runIbHealthCheckRepair,
  stopIbHealthCheckRepair,
} from "../../Store/Reducers/AdminSlice";
import { downloadBlob } from "../../Utils/Helper/downloadFile";
import "./style.scss";

const IB_HEALTH_REPAIR_LOG_POLL_MS = 2500;

interface RepairActivityEvent {
  at?: string;
  level?: string;
  message?: string;
}

interface RepairActivityProgress {
  triggered?: number;
  waiting?: number;
  processing?: number;
  retrying?: number;
  finished?: number;
  failed?: number;
  skipped?: number;
}

interface RepairActivityJob {
  status?: string;
  repairAll?: boolean;
  orgId?: string | null;
  orgName?: string | null;
  briefingDate?: string | null;
  parallelCount?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  error?: string | null;
  events?: RepairActivityEvent[];
  progress?: RepairActivityProgress | null;
}

export interface OrganizationFromAdmin {
  tenant_id?: string;
  organization: string;
  tenant_schema_name?: string;
}

interface NamedId {
  id?: string;
  name?: string;
}

interface IbHealthCheckIssue {
  type?: string;
  severity?: string;
  message?: string;
  count?: number;
  accounts?: NamedId[];
}

interface IbHealthCheckAccountRow {
  id?: string;
  name?: string;
  briefingStatus?: "present" | "missing" | string;
  inXFactor?: boolean;
  reasons?: string[];
}

interface IbHealthCheckOrg {
  id?: string;
  name?: string;
  slug?: string;
  issues?: IbHealthCheckIssue[];
  accounts?: IbHealthCheckAccountRow[];
  stats?: {
    ebAccounts?: number;
    xfactorAccounts?: number;
    accountsMissingInXFactor?: NamedId[];
    accountsExtraInXFactor?: NamedId[];
  };
  briefings?: {
    totalAccounts?: number;
    withBriefings?: number;
    missing?: number;
    missingAccounts?: NamedId[];
    date?: string;
  };
}

function formatBriefingDate(raw?: string): string {
  if (!raw) return "—";
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return raw;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

function formatScanDateTime(value: Date): string {
  return value.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatActivityTime(iso?: string | null): string {
  if (!iso) return "--/--/---- --:--:--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--/--/---- --:--:--";
  const datePart = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

function repairStatusLabel(status?: string): string {
  const key = String(status || "idle").toLowerCase();
  if (key === "running") return "Running";
  if (key === "completed") return "Completed";
  if (key === "stopped") return "Stopped";
  if (key === "failed") return "Failed";
  return "Idle";
}

const RepairActivityPanel: React.FC<{ job: RepairActivityJob }> = ({ job }) => {
  const logRef = useRef<HTMLDivElement | null>(null);
  const events = job.events || [];
  const statusKey = String(job.status || "idle").toLowerCase();
  const progress = job.progress;
  const scopeTitle = job.repairAll
    ? "All organizations"
    : job.orgName || job.orgId || "Selected organization";
  const hasProgress =
    !!progress &&
    (Number(progress.triggered) > 0 ||
      Number(progress.waiting) > 0 ||
      Number(progress.processing) > 0 ||
      Number(progress.retrying) > 0 ||
      Number(progress.finished) > 0 ||
      Number(progress.failed) > 0 ||
      Number(progress.skipped) > 0);

  useEffect(() => {
    const el = logRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [events.length]);

  return (
    <div className="admin-ib-health-check__activity">
      <div className="admin-ib-health-check__activity-head">
        <div className="admin-ib-health-check__activity-head-main">
          <strong>Repair activity — {scopeTitle}</strong>
          <div className="admin-ib-health-check__activity-meta">
            <span>
              Triggered {formatActivityTime(job.startedAt)}
              {job.briefingDate
                ? ` · Briefing date ${formatBriefingDate(job.briefingDate)}`
                : ""}
            </span>
            {job.parallelCount != null ? (
              <span>Parallel run: {job.parallelCount}</span>
            ) : null}
            {hasProgress ? (
              <span>Accounts triggered: {progress?.triggered ?? 0}</span>
            ) : null}
          </div>
        </div>
        <span
          className={`admin-ib-health-check__activity-status admin-ib-health-check__activity-status--${statusKey}`}
        >
          {repairStatusLabel(statusKey)}
        </span>
      </div>
      {hasProgress || job.parallelCount != null ? (
        <div className="admin-ib-health-check__activity-summary admin-ib-health-check__activity-summary--live">
          {job.parallelCount != null ? (
            <span className="admin-ib-health-check__activity-chip admin-ib-health-check__activity-chip--run">
              Parallel: {job.parallelCount}
            </span>
          ) : null}
          {hasProgress ? (
            <>
              <span className="admin-ib-health-check__activity-chip">
                Triggered: {progress?.triggered ?? 0}
              </span>
              <span className="admin-ib-health-check__activity-chip admin-ib-health-check__activity-chip--wait">
                Waiting: {progress?.waiting ?? 0}
              </span>
              <span className="admin-ib-health-check__activity-chip admin-ib-health-check__activity-chip--run">
                Processing: {progress?.processing ?? 0}
              </span>
              <span className="admin-ib-health-check__activity-chip admin-ib-health-check__activity-chip--retry">
                In retry: {progress?.retrying ?? 0}
              </span>
              <span className="admin-ib-health-check__activity-chip admin-ib-health-check__activity-chip--ok">
                Finished: {progress?.finished ?? 0}
              </span>
              <span
                className={`admin-ib-health-check__activity-chip${
                  (progress?.failed ?? 0) > 0
                    ? " admin-ib-health-check__activity-chip--bad"
                    : ""
                }`}
              >
                Failed: {progress?.failed ?? 0}
              </span>
              {(progress?.skipped ?? 0) > 0 ? (
                <span className="admin-ib-health-check__activity-chip admin-ib-health-check__activity-chip--skip">
                  Skipped: {progress?.skipped ?? 0}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      <div className="admin-ib-health-check__activity-log" ref={logRef}>
        {events.length === 0 ? (
          <div className="admin-ib-health-check__activity-line admin-ib-health-check__activity-line--info">
            <span className="admin-ib-health-check__activity-time">
              {formatActivityTime(job.startedAt)}
            </span>
            <span className="admin-ib-health-check__activity-msg">
              Waiting for repair progress…
            </span>
          </div>
        ) : (
          events.map((ev, idx) => {
            const level = String(ev.level || "info").toLowerCase();
            return (
              <div
                key={`${ev.at || "t"}-${idx}`}
                className={`admin-ib-health-check__activity-line admin-ib-health-check__activity-line--${level}`}
              >
                <span className="admin-ib-health-check__activity-time">
                  {formatActivityTime(ev.at)}
                </span>
                <span className="admin-ib-health-check__activity-msg">
                  {ev.message || "Update"}
                </span>
              </div>
            );
          })
        )}
      </div>
      {statusKey === "failed" && job.error && (
        <p className="admin-ib-health-check__activity-error">{job.error}</p>
      )}
    </div>
  );
};

interface AdminIbHealthCheckContainerProps {
  organizations: OrganizationFromAdmin[];
}

const ISSUE_LABELS: Record<string, string> = {
  missing_xfactor_org: "Missing org",
  missing_accounts_in_xfactor: "Missing accounts",
  account_name_mismatch: "Name mismatch",
  missing_xfactor_briefings: "Missing briefings",
};

const ISSUE_BADGE_TYPE: Record<string, "danger" | "warning" | "info"> = {
  missing_xfactor_org: "danger",
  missing_accounts_in_xfactor: "danger",
  account_name_mismatch: "warning",
  missing_xfactor_briefings: "warning",
};

type OrgAccountRow = {
  id: string;
  name: string;
  reasons: string[];
  briefingDate: string;
  briefingStatus: "present" | "missing";
};

const CSV_ESCAPED_DOUBLE_QUOTE = String.fromCharCode(34, 34);

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, CSV_ESCAPED_DOUBLE_QUOTE)}"`;
  }
  return value;
}

function buildOrgAccountsCsv(
  org: IbHealthCheckOrg,
  accounts: OrgAccountRow[]
): string {
  const headers = [
    "org_id",
    "org_name",
    "account_id",
    "account_name",
    "briefing_status",
    "briefing_date",
    "issues",
  ];
  const lines = accounts.map(account => {
    const status =
      account.briefingStatus === "present" ? "Generated" : "Missing";
    const issues = (account.reasons || [])
      .map(reason => ISSUE_LABELS[reason] || reason)
      .join("; ");
    return [
      org.id || "",
      org.name || "",
      account.id,
      account.name,
      status,
      account.briefingDate || "",
      issues,
    ]
      .map(cell => csvEscape(String(cell)))
      .join(",");
  });
  return [headers.join(","), ...lines].join("\n");
}

function downloadOrgAccountsCsv(
  org: IbHealthCheckOrg,
  accounts: OrgAccountRow[]
): void {
  const slug = String(org.name || org.id || "org")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  const datePart = (accounts[0]?.briefingDate || "").slice(0, 10) || "scan";
  const csv = buildOrgAccountsCsv(org, accounts);
  downloadBlob(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    `ib-health-check-${slug || "org"}-${datePart}.csv`
  );
}

function collectOrgAccounts(
  org: IbHealthCheckOrg,
  briefingDate?: string
): OrgAccountRow[] {
  const checkedDate = briefingDate || org.briefings?.date || "";
  if ((org.accounts || []).length > 0) {
    return (org.accounts || [])
      .map(account => {
        const id = String(account.id || "").trim();
        if (!id) return null;
        const status =
          String(account.briefingStatus || "").toLowerCase() === "present"
            ? "present"
            : "missing";
        return {
          id,
          name: account.name || "—",
          reasons: Array.isArray(account.reasons) ? account.reasons : [],
          briefingDate: checkedDate,
          briefingStatus: status as "present" | "missing",
        };
      })
      .filter((row): row is OrgAccountRow => Boolean(row));
  }

  // Fallback for older scan payloads without accounts census.
  const byId = new Map<string, OrgAccountRow>();
  for (const issue of org.issues || []) {
    const reason = issue.type || "issue";
    for (const account of issue.accounts || []) {
      const id = account.id || "";
      if (!id) continue;
      const existing = byId.get(id);
      if (existing) {
        if (!existing.reasons.includes(reason)) existing.reasons.push(reason);
      } else {
        byId.set(id, {
          id,
          name: account.name || "—",
          reasons: [reason],
          briefingDate: checkedDate,
          briefingStatus: "missing",
        });
      }
    }
  }
  return Array.from(byId.values());
}

function buildAccountColumns(opts: {
  busy: boolean;
  notConfigured: boolean;
  generatingAccountIds: string[];
  selectedIds: Set<string>;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;
  onToggleRow: (accountId: string, checked: boolean) => void;
  onToggleAllFiltered: (checked: boolean) => void;
  onGenerate: (account: OrgAccountRow) => void;
}) {
  const {
    busy,
    notConfigured,
    generatingAccountIds,
    selectedIds,
    allFilteredSelected,
    someFilteredSelected,
    onToggleRow,
    onToggleAllFiltered,
    onGenerate,
  } = opts;
  return [
    {
      key: "select",
      title: (
        <input
          type="checkbox"
          className="admin-ib-health-check__row-check"
          checked={allFilteredSelected}
          ref={el => {
            if (el) {
              el.indeterminate = someFilteredSelected && !allFilteredSelected;
            }
          }}
          onChange={e => onToggleAllFiltered(e.target.checked)}
          disabled={busy || notConfigured}
          aria-label="Select all visible accounts"
          onClick={e => e.stopPropagation()}
        />
      ),
      width: "44px",
      render: (_value: unknown, record: OrgAccountRow) => (
        <input
          type="checkbox"
          className="admin-ib-health-check__row-check"
          checked={selectedIds.has(record.id)}
          onChange={e => onToggleRow(record.id, e.target.checked)}
          disabled={busy || notConfigured || !record.id}
          aria-label={`Select ${record.name || record.id}`}
          onClick={e => e.stopPropagation()}
        />
      ),
    },
    {
      key: "name",
      title: "Account",
      render: (value: string) => value || "—",
    },
    {
      key: "id",
      title: "Account ID",
      render: (value: string) => (
        <span className="admin-ib-health-check__mono">{value || "—"}</span>
      ),
    },
    {
      key: "briefingDate",
      title: "Briefing date",
      render: (value: string) => (
        <span className="admin-ib-health-check__mono">
          {formatBriefingDate(value)}
        </span>
      ),
    },
    {
      key: "briefingStatus",
      title: "Briefing",
      render: (value: string) => (
        <Badge
          type={value === "present" ? "success" : "warning"}
          label={value === "present" ? "Generated" : "Missing"}
        />
      ),
    },
    {
      key: "reasons",
      title: "Issue",
      render: (value: string[]) =>
        (value || []).length === 0 ? (
          <span className="admin-ib-health-check__muted">None</span>
        ) : (
          <div className="admin-ib-health-check__badges">
            {(value || []).map(reason => (
              <Badge
                key={reason}
                type={ISSUE_BADGE_TYPE[reason] || "warning"}
                label={ISSUE_LABELS[reason] || reason}
              />
            ))}
          </div>
        ),
    },
    {
      key: "actions",
      title: "Action",
      render: (_value: unknown, record: OrgAccountRow) => {
        const loading = generatingAccountIds.includes(record.id);
        const label =
          record.briefingStatus === "present" ? "Force generate" : "Generate";
        return (
          <Button
            variant={
              record.briefingStatus === "present" ? "secondary" : "primary"
            }
            onClick={() => onGenerate(record)}
            disabled={busy || notConfigured || !record.id}
            loading={loading}
          >
            {label}
          </Button>
        );
      },
    },
  ];
}

const OrgAccountCard: React.FC<{
  org: IbHealthCheckOrg;
  briefingDate?: string;
  busy: boolean;
  repairingThis: boolean;
  generatingAccountIds: string[];
  notConfigured: boolean;
  showRepairButton?: boolean;
  onRepair?: () => void;
  onGenerateAccount: (account: OrgAccountRow) => void;
  onGenerateSelected: (accounts: OrgAccountRow[], force: boolean) => void;
}> = ({
  org,
  briefingDate,
  busy,
  repairingThis,
  generatingAccountIds,
  notConfigured,
  showRepairButton = true,
  onRepair,
  onGenerateAccount,
  onGenerateSelected,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "present" | "missing"
  >("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const accounts = useMemo(
    () => collectOrgAccounts(org, briefingDate),
    [org, briefingDate]
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return accounts.filter(row => {
      if (statusFilter !== "all" && row.briefingStatus !== statusFilter) {
        return false;
      }
      if (!needle) return true;
      return (
        row.name.toLowerCase().includes(needle) ||
        row.id.toLowerCase().includes(needle)
      );
    });
  }, [accounts, query, statusFilter]);

  useEffect(() => {
    const valid = new Set(accounts.map(row => row.id));
    setSelectedIds(prev => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach(id => {
        if (valid.has(id)) next.add(id);
        else changed = true;
      });
      return changed || next.size !== prev.size ? next : prev;
    });
  }, [accounts]);

  const presentCount = accounts.filter(
    row => row.briefingStatus === "present"
  ).length;
  const missingCount = accounts.length - presentCount;
  const allFilteredSelected =
    filtered.length > 0 && filtered.every(row => selectedIds.has(row.id));
  const someFilteredSelected = filtered.some(row => selectedIds.has(row.id));
  const selectedAccounts = accounts.filter(row => selectedIds.has(row.id));
  const selectedMissing = selectedAccounts.filter(
    row => row.briefingStatus === "missing"
  );
  const bulkBusy =
    generatingAccountIds.length > 0 &&
    generatingAccountIds.some(id => selectedIds.has(id));

  const accountColumns = useMemo(
    () =>
      buildAccountColumns({
        busy,
        notConfigured,
        generatingAccountIds,
        selectedIds,
        allFilteredSelected,
        someFilteredSelected,
        onToggleRow: (accountId, checked) => {
          setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) next.add(accountId);
            else next.delete(accountId);
            return next;
          });
        },
        onToggleAllFiltered: checked => {
          setSelectedIds(prev => {
            const next = new Set(prev);
            filtered.forEach(row => {
              if (checked) next.add(row.id);
              else next.delete(row.id);
            });
            return next;
          });
        },
        onGenerate: onGenerateAccount,
      }),
    [
      busy,
      notConfigured,
      generatingAccountIds,
      selectedIds,
      allFilteredSelected,
      someFilteredSelected,
      filtered,
      onGenerateAccount,
    ]
  );

  return (
    <div className="admin-ib-health-check__org-card">
      <div
        className="admin-ib-health-check__org-head"
        onClick={() => setExpanded(open => !open)}
      >
        <div className="admin-ib-health-check__org-head-main">
          <span className="admin-ib-health-check__dot" />
          <div>
            <p className="admin-ib-health-check__org-name">
              {org.name || "Unknown org"}
            </p>
            <p className="admin-ib-health-check__org-id">
              Organization ID {org.id || "—"}
              {(briefingDate || org.briefings?.date) && (
                <>
                  {" · "}
                  Briefing date{" "}
                  {formatBriefingDate(briefingDate || org.briefings?.date)}
                </>
              )}
              {accounts.length > 0 && (
                <>
                  {" · "}
                  {accounts.length} account{accounts.length === 1 ? "" : "s"} (
                  {presentCount} generated, {missingCount} missing)
                </>
              )}
            </p>
          </div>
          <div className="admin-ib-health-check__badges">
            {(org.issues || []).length === 0 ? (
              <Badge type="success" label="Healthy" />
            ) : (
              (org.issues || []).map((issue, idx) => (
                <Badge
                  key={`${issue.type}-${idx}`}
                  type={ISSUE_BADGE_TYPE[issue.type || ""] || "warning"}
                  label={`${ISSUE_LABELS[issue.type || ""] || issue.type}${
                    issue.count ? ` (${issue.count})` : ""
                  }`}
                />
              ))
            )}
          </div>
        </div>
        <div
          className="admin-ib-health-check__org-actions"
          onClick={event => event.stopPropagation()}
        >
          {showRepairButton && onRepair && (
            <Button
              variant="primary"
              onClick={onRepair}
              disabled={busy || notConfigured || !org.id}
              loading={repairingThis && generatingAccountIds.length === 0}
            >
              Repair This Organization
            </Button>
          )}
          <button
            type="button"
            className="admin-ib-health-check__chevron"
            aria-label={expanded ? "Collapse" : "Expand"}
            onClick={() => setExpanded(open => !open)}
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="admin-ib-health-check__org-body">
          {(org.issues || []).map((issue, idx) => (
            <p
              key={`${issue.type}-${idx}`}
              className="admin-ib-health-check__issue"
            >
              {issue.message}
            </p>
          ))}
          <p className="admin-ib-health-check__issue">
            Select accounts for bulk Generate / Force, or use the row actions.
            Export CSV for ticket handoff.
          </p>
          {accounts.length > 0 ? (
            <>
              <div className="admin-ib-health-check__filters">
                <div className="admin-ib-health-check__search">
                  <SearchInput
                    placeholder="Search account name or ID"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    rounded="normal"
                  />
                </div>
                <div className="admin-ib-health-check__status-filter">
                  <Select
                    name={`ibBriefingStatus-${org.id || org.name || "org"}`}
                    label="Briefing status"
                    value={statusFilter}
                    onChange={e =>
                      setStatusFilter(
                        String(e.target.value) as "all" | "present" | "missing"
                      )
                    }
                    options={[
                      { label: "All", value: "all" },
                      { label: "Generated", value: "present" },
                      { label: "Missing", value: "missing" },
                    ]}
                    isShowPlaceHolder={false}
                  />
                </div>
              </div>
              <div className="admin-ib-health-check__bulk-bar">
                <span className="admin-ib-health-check__bulk-count">
                  {selectedAccounts.length} selected
                  {selectedMissing.length > 0
                    ? ` (${selectedMissing.length} missing)`
                    : ""}
                </span>
                <div className="admin-ib-health-check__bulk-actions">
                  <Button
                    variant="primary"
                    onClick={() => onGenerateSelected(selectedMissing, false)}
                    disabled={
                      busy ||
                      notConfigured ||
                      selectedMissing.length === 0 ||
                      !org.id
                    }
                    loading={bulkBusy && selectedMissing.length > 0}
                  >
                    Generate selected
                    {selectedMissing.length > 0
                      ? ` (${selectedMissing.length})`
                      : ""}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => onGenerateSelected(selectedAccounts, true)}
                    disabled={
                      busy ||
                      notConfigured ||
                      selectedAccounts.length === 0 ||
                      !org.id
                    }
                    loading={bulkBusy}
                  >
                    Force selected
                    {selectedAccounts.length > 0
                      ? ` (${selectedAccounts.length})`
                      : ""}
                  </Button>
                  <Button
                    variant="gray"
                    onClick={() => downloadOrgAccountsCsv(org, accounts)}
                    disabled={accounts.length === 0}
                    icon={<Download size={16} />}
                  >
                    Export CSV
                  </Button>
                </div>
              </div>
              <Table
                columns={accountColumns}
                data={filtered}
                rowKey={row => row.id}
                pagination
                defaultRowsPerPage={10}
                rowsPerPageOptions={[10, 25, 50]}
                scrollable
                maxHeight="320px"
                stickyLastColumn
                emptyContent="No accounts match that search or filter."
              />
            </>
          ) : (
            <p className="admin-ib-health-check__card-copy">
              No ExecBridge accounts found for this organization.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const AdminIbHealthCheckContainer: React.FC<
  AdminIbHealthCheckContainerProps
> = ({ organizations }) => {
  const dispatch = useAppDispatch();
  const {
    ibHealthCheckReport: report,
    ibHealthCheckLoading: scanning,
    ibHealthCheckRepairLoading: repairing,
    ibHealthCheckRepairJob: repairJob,
    ibHealthCheckError: error,
  } = useAppSelector((state: { admin: any }) => state.admin);

  const [selectedOrg, setSelectedOrg] = useState("all");
  /** Scope of the last successful scan: "all", a tenant id, or null before any scan. */
  const [lastScanScope, setLastScanScope] = useState<"all" | string | null>(
    null
  );
  const [lastRunTime, setLastRunTime] = useState<Date | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmRepairAll, setConfirmRepairAll] = useState(false);
  const [confirmOrgId, setConfirmOrgId] = useState<string | null>(null);
  const [confirmOrgName, setConfirmOrgName] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [forceRegenerateAll, setForceRegenerateAll] = useState(false);
  const [accountConfirmOpen, setAccountConfirmOpen] = useState(false);
  const [accountGenerateTarget, setAccountGenerateTarget] = useState<{
    orgId: string;
    orgName: string;
    accounts: OrgAccountRow[];
    force: boolean;
  } | null>(null);
  const [generatingAccountIds, setGeneratingAccountIds] = useState<string[]>(
    []
  );

  const repairJobStatus = String(repairJob?.status || "").toLowerCase();
  const repairLogIsLive = repairing || repairJobStatus === "running";
  const hasRepairLogEvents = (repairJob?.events || []).length > 0;
  const canStopRepair = repairLogIsLive && !stopLoading;
  const repairLogIsTerminal = ["completed", "failed", "stopped"].includes(
    repairJobStatus
  );

  const orgOptions = useMemo(
    () => [
      { label: "All organizations", value: "all" },
      ...organizations.map(org => ({
        label: org.organization,
        value: org.tenant_id || org.organization,
      })),
    ],
    [organizations]
  );

  const issueOrgs = useMemo<IbHealthCheckOrg[]>(() => {
    const orgs: IbHealthCheckOrg[] = report?.organizations || [];
    return orgs.filter(org => (org.issues || []).length > 0);
  }, [report]);

  const healthyOrgs = useMemo<IbHealthCheckOrg[]>(() => {
    const orgs: IbHealthCheckOrg[] = report?.organizations || [];
    return orgs.filter(org => (org.issues || []).length === 0);
  }, [report]);

  const notConfigured = Boolean(error && /not configured/i.test(String(error)));
  const busy = scanning || repairing;

  const handleScan = async () => {
    const org = selectedOrg === "all" ? null : selectedOrg;
    const scanScope: "all" | string =
      selectedOrg === "all" ? "all" : selectedOrg;
    try {
      await dispatch(runIbHealthCheck({ org }) as any).unwrap();
      setLastScanScope(scanScope);
      setLastRunTime(new Date());
    } catch {
      // Toast is handled in the slice.
    }
  };

  const rescanAfterRepair = async () => {
    if (lastScanScope == null) return;
    const org = lastScanScope === "all" ? null : lastScanScope;
    try {
      await dispatch(runIbHealthCheck({ org }) as any).unwrap();
      setLastRunTime(new Date());
    } catch {
      // Toast is handled in the slice.
    }
  };

  const showRepairAll = issueOrgs.length > 0 && lastScanScope === "all";

  const openRepairOne = (orgId: string, orgName: string) => {
    setConfirmRepairAll(false);
    setConfirmOrgId(orgId);
    setConfirmOrgName(orgName);
    setConfirmOpen(true);
  };

  const openRepairAll = () => {
    setConfirmRepairAll(true);
    setConfirmOrgId(null);
    setConfirmOrgName("");
    setConfirmOpen(true);
  };

  const openGenerateAccount = (
    org: IbHealthCheckOrg,
    account: OrgAccountRow
  ) => {
    if (!org.id || !account.id) return;
    setAccountGenerateTarget({
      orgId: org.id,
      orgName: org.name || "organization",
      accounts: [account],
      force: account.briefingStatus === "present",
    });
    setAccountConfirmOpen(true);
  };

  const openGenerateSelected = (
    org: IbHealthCheckOrg,
    accounts: OrgAccountRow[],
    force: boolean
  ) => {
    if (!org.id || accounts.length === 0) return;
    setAccountGenerateTarget({
      orgId: org.id,
      orgName: org.name || "organization",
      accounts,
      force,
    });
    setAccountConfirmOpen(true);
  };

  const handleConfirmRepair = async () => {
    const force = forceRegenerateAll;
    setConfirmOpen(false);
    try {
      if (confirmRepairAll) {
        await dispatch(
          runIbHealthCheckRepair({
            repairAll: true,
            forceRegenerateAll: force,
          }) as any
        ).unwrap();
      } else if (confirmOrgId) {
        await dispatch(
          runIbHealthCheckRepair({
            orgId: confirmOrgId,
            forceRegenerateAll: force,
          }) as any
        ).unwrap();
      } else {
        return;
      }
      await rescanAfterRepair();
    } catch {
      // Toast is handled in the slice.
    }
  };

  const handleConfirmAccountGenerate = async () => {
    if (!accountGenerateTarget) return;
    const { orgId, accounts } = accountGenerateTarget;
    const accountIds = accounts.map(account => account.id).filter(Boolean);
    if (accountIds.length === 0) return;
    setAccountConfirmOpen(false);
    setGeneratingAccountIds(accountIds);
    setConfirmOrgId(orgId);
    try {
      await dispatch(
        runIbHealthCheckRepair({
          orgId,
          accountIds,
          generateMissingBriefings: true,
        }) as any
      ).unwrap();
      await rescanAfterRepair();
    } catch {
      // Toast is handled in the slice.
    } finally {
      setGeneratingAccountIds([]);
    }
  };

  const handleDoneRepairLog = async () => {
    setLogOpen(false);
    if (!repairLogIsTerminal) return;
    // Skip if auto re-scan after repair already refreshed past this job.
    const finishedAt = repairJob?.finishedAt
      ? new Date(repairJob.finishedAt).getTime()
      : 0;
    const lastScanMs = lastRunTime?.getTime() ?? 0;
    if (finishedAt > 0 && lastScanMs >= finishedAt) return;
    await rescanAfterRepair();
  };

  const openRepairLog = async () => {
    setLogOpen(true);
    setLogLoading(true);
    try {
      await dispatch(fetchIbHealthCheckRepairJob() as any).unwrap();
    } catch {
      // Keep modal open; empty/error state is handled in the panel body.
    } finally {
      setLogLoading(false);
    }
  };

  const handleConfirmStopRepair = async () => {
    setStopConfirmOpen(false);
    setStopLoading(true);
    try {
      await dispatch(stopIbHealthCheckRepair() as any).unwrap();
      await rescanAfterRepair();
    } catch {
      // Toast is handled in the slice.
    } finally {
      setStopLoading(false);
    }
  };

  useEffect(() => {
    if (!logOpen) return;
    if (!repairLogIsLive) return;
    const timer = window.setInterval(() => {
      dispatch(fetchIbHealthCheckRepairJob() as any);
    }, IB_HEALTH_REPAIR_LOG_POLL_MS);
    return () => window.clearInterval(timer);
  }, [dispatch, logOpen, repairLogIsLive]);

  return (
    <div className="admin-ib-health-check">
      <div className="admin-ib-health-check__hero">
        <div className="admin-ib-health-check__hero-title-row">
          <Activity size={28} />
          <div>
            <h2 className="admin-ib-health-check__title">IB Health Check</h2>
            <p className="admin-ib-health-check__subtitle">
              Scan ExecBridge against Intelligence Briefings to find missing
              organizations, accounts, or missing last-Sunday briefings.
            </p>
          </div>
        </div>
      </div>

      <div className="admin-ib-health-check__card">
        <h3 className="admin-ib-health-check__card-title">Run a scan</h3>
        <p className="admin-ib-health-check__card-copy">
          Choose one organization or all, then scan. Large orgs run in the
          background so the request does not time out — this page waits for the
          full report. Repair stays disabled until a scan finds gaps.
        </p>
        <div className="admin-ib-health-check__run-row">
          <div className="admin-ib-health-check__select">
            <Select
              name="ibHealthCheckOrg"
              label="Organization"
              value={selectedOrg}
              onChange={e => setSelectedOrg(String(e.target.value))}
              options={orgOptions}
              disabled={busy}
              isShowPlaceHolder={false}
            />
          </div>
          <Button
            variant="primary"
            onClick={handleScan}
            disabled={busy}
            loading={scanning}
            icon={<RefreshCw size={16} />}
          >
            Run scan
          </Button>
          <Button
            variant="gray"
            onClick={openRepairLog}
            disabled={notConfigured}
            icon={<ScrollText size={16} />}
          >
            {repairLogIsLive ? "Repair log (live)" : "Repair log"}
          </Button>
        </div>
        {lastRunTime && (
          <p className="admin-ib-health-check__meta">
            Last scan: {formatScanDateTime(lastRunTime)}
            {report?.briefings?.date && (
              <>
                {" · "}
                Briefing date checked:{" "}
                {formatBriefingDate(report.briefings.date)}
              </>
            )}
          </p>
        )}
      </div>

      {error && (
        <div className="admin-ib-health-check__error" role="alert">
          {error}
        </div>
      )}

      {!report && !error && (
        <div className="admin-ib-health-check__card">
          <h3 className="admin-ib-health-check__card-title">
            What this checks
          </h3>
          <div className="admin-ib-health-check__steps">
            <div className="admin-ib-health-check__step">
              <Building2 size={20} />
              <strong>1. Org</strong>
              <span>
                Does Intelligence Briefings already have this organization?
              </span>
            </div>
            <div className="admin-ib-health-check__step">
              <CheckCircle size={20} />
              <strong>2. Accounts</strong>
              <span>
                Does each ExecBridge account exist under that organization in
                Intelligence Briefings?
              </span>
            </div>
            <div className="admin-ib-health-check__step">
              <FileText size={20} />
              <strong>3. Briefings</strong>
              <span>Is the last-Sunday briefing present for each account?</span>
            </div>
          </div>
        </div>
      )}

      {report && (
        <>
          <div className="admin-ib-health-check__summary">
            <div className="admin-ib-health-check__stat">
              <p className="admin-ib-health-check__stat-label">
                Organizations in Intelligence Briefings
              </p>
              <p className="admin-ib-health-check__stat-value">
                {report.summary?.xfactorOrgs ?? 0}
                <span> / {report.summary?.totalOrgs ?? 0}</span>
              </p>
            </div>
            <div className="admin-ib-health-check__stat">
              <p className="admin-ib-health-check__stat-label">
                Accounts in Intelligence Briefings
              </p>
              <p className="admin-ib-health-check__stat-value">
                {report.summary?.xfactorAccounts ?? 0}
                <span> / {report.summary?.totalAccounts ?? 0}</span>
              </p>
            </div>
            <div className="admin-ib-health-check__stat">
              <p className="admin-ib-health-check__stat-label">
                Missing briefings
              </p>
              <p className="admin-ib-health-check__stat-value">
                {report.briefings?.missing ?? 0}
              </p>
            </div>
            <div className="admin-ib-health-check__stat">
              <p className="admin-ib-health-check__stat-label">Issues</p>
              <p className="admin-ib-health-check__stat-value">
                {report.summary?.totalIssues ?? 0}
              </p>
            </div>
          </div>

          <div className="admin-ib-health-check__section">
            <div className="admin-ib-health-check__section-head">
              <div>
                <h3 className="admin-ib-health-check__section-title">
                  <AlertTriangle size={16} />
                  Needs repair ({issueOrgs.length})
                </h3>
                <p className="admin-ib-health-check__card-copy">
                  Expand an organization to search missing accounts or
                  briefings. Use Repair This Organization for one org, or Repair
                  All when you scanned all organizations. Open Repair log for
                  live progress or the last cached run, then re-scan when
                  finished.
                </p>
              </div>
              <div className="admin-ib-health-check__section-head-action">
                <Button
                  variant="gray"
                  onClick={openRepairLog}
                  disabled={notConfigured}
                  icon={<ScrollText size={16} />}
                >
                  {repairLogIsLive ? "Repair log (live)" : "Repair log"}
                </Button>
                {showRepairAll && (
                  <Button
                    variant="primary"
                    onClick={openRepairAll}
                    disabled={busy || notConfigured}
                    loading={repairing && confirmRepairAll}
                  >
                    Repair All
                  </Button>
                )}
              </div>
            </div>
            {issueOrgs.length > 0 && (
              <label
                className={`admin-ib-health-check__force-banner${
                  forceRegenerateAll
                    ? " admin-ib-health-check__force-banner--on"
                    : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={forceRegenerateAll}
                  disabled={busy || notConfigured}
                  onChange={e => setForceRegenerateAll(e.target.checked)}
                />
                <span>
                  <strong>Force regenerate all accounts</strong>
                  <span className="admin-ib-health-check__force-banner-hint">
                    When checked, Repair / Repair all overwrites last-Sunday
                    briefings for every account in scope (not only missing or
                    partial). Higher load on Intelligence Briefings.
                  </span>
                </span>
              </label>
            )}
            {issueOrgs.length === 0 ? (
              <div className="admin-ib-health-check__ok">
                <CheckCircle size={18} />
                All scanned organizations match Intelligence Briefings.
              </div>
            ) : (
              <div className="admin-ib-health-check__org-list">
                {issueOrgs.map(org => (
                  <OrgAccountCard
                    key={org.id || org.name}
                    org={org}
                    briefingDate={report?.briefings?.date}
                    busy={busy}
                    notConfigured={notConfigured}
                    repairingThis={
                      repairing &&
                      confirmOrgId === org.id &&
                      generatingAccountIds.length === 0
                    }
                    generatingAccountIds={
                      confirmOrgId === org.id ? generatingAccountIds : []
                    }
                    onRepair={() => openRepairOne(org.id || "", org.name || "")}
                    onGenerateAccount={account =>
                      openGenerateAccount(org, account)
                    }
                    onGenerateSelected={(accounts, force) =>
                      openGenerateSelected(org, accounts, force)
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {healthyOrgs.length > 0 && (
            <div className="admin-ib-health-check__section">
              <div className="admin-ib-health-check__section-head">
                <div>
                  <h3 className="admin-ib-health-check__section-title">
                    <CheckCircle size={16} />
                    Healthy ({healthyOrgs.length})
                  </h3>
                  <p className="admin-ib-health-check__card-copy">
                    Expand an organization to force-generate briefings for any
                    account, even when a briefing already exists.
                  </p>
                </div>
              </div>
              <div className="admin-ib-health-check__org-list">
                {healthyOrgs.map(org => (
                  <OrgAccountCard
                    key={org.id || org.name}
                    org={org}
                    briefingDate={report?.briefings?.date}
                    busy={busy}
                    notConfigured={notConfigured}
                    repairingThis={false}
                    generatingAccountIds={
                      confirmOrgId === org.id ? generatingAccountIds : []
                    }
                    showRepairButton={false}
                    onGenerateAccount={account =>
                      openGenerateAccount(org, account)
                    }
                    onGenerateSelected={(accounts, force) =>
                      openGenerateSelected(org, accounts, force)
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <CommonModal
        isOpen={confirmOpen}
        toggle={() => setConfirmOpen(false)}
        title={
          confirmRepairAll
            ? "Repair All organizations with issues?"
            : `Repair This Organization (${confirmOrgName || "selected"})?`
        }
        footer={
          <>
            <Button variant="gray" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmRepair}>
              {forceRegenerateAll
                ? confirmRepairAll
                  ? "Force repair all"
                  : "Force repair"
                : confirmRepairAll
                  ? "Repair All"
                  : "Repair This Organization"}
            </Button>
          </>
        }
      >
        <p>
          Repair starts in the background. It creates missing Intelligence
          Briefings organizations and accounts
          {forceRegenerateAll
            ? ", then force-regenerates briefings for every account"
            : ", then generates missing briefings (failed generates are retried)"}
          {confirmRepairAll
            ? " for every organization with issues in this report."
            : ` for ${confirmOrgName || "this organization"}.`}
          <br />
          <br />
          Open <strong>Repair log</strong> to watch live progress. When it
          finishes, the report re-scans automatically so counts update.
          {!forceRegenerateAll && (
            <>
              {" "}
              If a briefing already exists, Intelligence Briefings may not
              overwrite it unless you enable force regenerate.
            </>
          )}
        </p>
        <label className="admin-ib-health-check__force-regen mt-4 flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1"
            checked={forceRegenerateAll}
            onChange={e => setForceRegenerateAll(e.target.checked)}
          />
          <span>
            <strong>Force regenerate all accounts</strong>
            <span className="block text-sm text-gray-600 mt-0.5">
              Overwrites briefings for every account in scope (including
              complete ones). Slower and higher load on Intelligence Briefings —
              use when soft repair is not enough.
            </span>
          </span>
        </label>
      </CommonModal>

      <CommonModal
        isOpen={accountConfirmOpen}
        toggle={() => setAccountConfirmOpen(false)}
        title={
          accountGenerateTarget?.force
            ? accountGenerateTarget.accounts.length > 1
              ? "Force generate selected briefings?"
              : "Force generate briefing?"
            : accountGenerateTarget && accountGenerateTarget.accounts.length > 1
              ? "Generate selected briefings?"
              : "Generate briefing?"
        }
        footer={
          <>
            <Button variant="gray" onClick={() => setAccountConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmAccountGenerate}>
              {accountGenerateTarget?.force
                ? accountGenerateTarget.accounts.length > 1
                  ? `Force selected (${accountGenerateTarget.accounts.length})`
                  : "Force generate"
                : accountGenerateTarget &&
                    accountGenerateTarget.accounts.length > 1
                  ? `Generate selected (${accountGenerateTarget.accounts.length})`
                  : "Generate"}
            </Button>
          </>
        }
      >
        <p>
          {accountGenerateTarget?.force
            ? "This will overwrite Intelligence Briefings digests for "
            : "This will generate Intelligence Briefings digests for "}
          <strong>
            {accountGenerateTarget &&
            accountGenerateTarget.accounts.length === 1
              ? accountGenerateTarget.accounts[0].name || "this account"
              : `${accountGenerateTarget?.accounts.length || 0} selected account${
                  (accountGenerateTarget?.accounts.length || 0) === 1 ? "" : "s"
                }`}
          </strong>
          {" in "}
          <strong>
            {accountGenerateTarget?.orgName || "this organization"}
          </strong>
          .
          <br />
          <br />
          Open <strong>Repair log</strong> to watch progress. When it finishes,
          the report re-scans automatically.
        </p>
      </CommonModal>

      <CommonModal
        isOpen={logOpen}
        toggle={() => setLogOpen(false)}
        title="Repair log"
        subtitle={
          repairLogIsLive
            ? "Live — updates while repair is running on this server."
            : repairJobStatus === "stopped"
              ? "Stopped — work already done was kept. Click Done to refresh the report."
              : hasRepairLogEvents
                ? "Last repair activity still in server memory (this worker)."
                : "No repair activity in memory yet for this server."
        }
        size="5xl"
        className="admin-ib-health-check__log-modal"
        contentClassName="overflow-hidden flex flex-col admin-ib-health-check__log-modal-body"
        footer={
          <>
            {canStopRepair && (
              <Button
                variant="danger"
                onClick={() => setStopConfirmOpen(true)}
                loading={stopLoading}
                disabled={stopLoading}
              >
                Stop repair
              </Button>
            )}
            {repairLogIsTerminal ? (
              <Button
                variant="primary"
                onClick={handleDoneRepairLog}
                loading={scanning}
                disabled={scanning}
              >
                Done
              </Button>
            ) : (
              <Button variant="gray" onClick={() => setLogOpen(false)}>
                Close
              </Button>
            )}
          </>
        }
      >
        {logLoading && !hasRepairLogEvents ? (
          <p className="admin-ib-health-check__card-copy">
            Loading repair log…
          </p>
        ) : repairJob &&
          (hasRepairLogEvents ||
            ["running", "completed", "failed", "stopped"].includes(
              repairJobStatus
            )) ? (
          <RepairActivityPanel job={repairJob} />
        ) : (
          <p className="admin-ib-health-check__card-copy">
            No repair log yet. After you run Repair, open this again for live
            lines. After logout/login you can still see the last run if this API
            worker has not restarted.
          </p>
        )}
      </CommonModal>

      <CommonModal
        isOpen={stopConfirmOpen}
        toggle={() => setStopConfirmOpen(false)}
        title="Stop repair?"
        footer={
          <>
            <Button variant="gray" onClick={() => setStopConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmStopRepair}>
              Stop repair
            </Button>
          </>
        }
      >
        <p>
          Stop repair? Work already done stays. In-flight generates may still
          finish. Remaining accounts will not be started. The report will
          re-scan after stop so counts update.
        </p>
      </CommonModal>
    </div>
  );
};

export default AdminIbHealthCheckContainer;
